import React, { useState, useEffect, useRef, useCallback } from 'react';
import { geoSearchService } from '../../utils/nominatimGeocoding';
import './AddressAutocomplete.css';

function AddressAutocomplete({ value, onChange, onLocationSelect, disabled, placeholder }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Format short address from Google Places suggestion
  const formatShortAddress = (suggestion) => {
    // Google Places zwraca description w formacie: "ulica, miasto, kraj"
    // Wyciągamy pierwsze dwie części (ulica i miasto)
    const description = suggestion.description || '';
    const mainText = suggestion.structured_formatting?.main_text || '';
    const secondaryText = suggestion.structured_formatting?.secondary_text || '';
    
    if (mainText && secondaryText) {
      // Jeśli mamy structured_formatting, użyj głównego tekstu + miasta z secondary.
      // Wcześniej używaliśmy pełnego `description`, co potrafiło duplikować fragmenty adresu.
      const city = secondaryText.split(',')[0]; // Pierwsza część secondary to zwykle miasto
      return `${mainText}, ${city}`;
    }
    
    if (description) {
      // Wyciągnij pierwsze dwie części z description
      const parts = description.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        return parts.slice(0, 2).join(', ');
      }
      return description;
    }
    
    return suggestion.description || '';
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchAddresses = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await geoSearchService.autocomplete(query, {
        types: ['address'],
        componentRestrictions: { country: 'pl' },
      });
      
      if (response.predictions) {
        setSuggestions(response.predictions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      console.error('Error searching addresses:', err);
      // Silently fail - user can continue typing
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear selected location when user types
    if (selectedLocation) {
      setSelectedLocation(null);
      if (onLocationSelect) {
        onLocationSelect(null);
      }
    }

    // Debounce search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      searchAddresses(newValue);
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion) => {
    const shortAddress = formatShortAddress(suggestion);
    onChange(shortAddress);
    setShowSuggestions(false);
    
    try {
      let lat = suggestion.lat != null ? Number(suggestion.lat) : null;
      let lng = suggestion.lon != null ? Number(suggestion.lon) : null;
      let fullAddress = suggestion.description || shortAddress;

      if (lat == null || lng == null) {
        const placeDetails = await geoSearchService.getPlaceDetails(suggestion.osm_ids || suggestion.place_id, {
          language: 'pl',
          fields: 'geometry,formatted_address',
        });
        const loc = placeDetails.geometry?.location;
        lat = typeof loc?.lat === 'function' ? loc.lat() : loc?.lat ?? null;
        lng = typeof loc?.lng === 'function' ? loc.lng() : loc?.lng ?? null;
        fullAddress = placeDetails.formatted_address || fullAddress;
      }

      const location = {
        address: shortAddress,
        fullAddress,
        lat,
        lng,
        placeId: suggestion.place_id,
      };

      setSelectedLocation(location);
      if (onLocationSelect) {
        onLocationSelect(location);
      }
    } catch (err) {
      console.error('Error getting place details:', err);
      // Even if we can't get coordinates, still set the address
      const location = {
        address: shortAddress,
        lat: null,
        lng: null,
        placeId: suggestion.place_id,
      };
      
      setSelectedLocation(location);
      if (onLocationSelect) {
        onLocationSelect(location);
      }
    }
  };

  return (
    <div className="address-autocomplete">
      <div className="address-autocomplete-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          disabled={disabled}
          placeholder={placeholder}
          className="address-autocomplete-input"
        />
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul ref={suggestionsRef} className="address-autocomplete-suggestions">
          {suggestions.map((suggestion, index) => {
            const shortAddress = formatShortAddress(suggestion);
            const fullAddress = suggestion.description || '';
            return (
              <li
                key={suggestion.place_id || index}
                className="address-autocomplete-suggestion"
                onClick={() => handleSelectSuggestion(suggestion)}
                title={fullAddress}
              >
                <span className="suggestion-icon">📍</span>
                <div className="suggestion-content">
                  <span className="suggestion-text">{shortAddress}</span>
                  {suggestion.structured_formatting?.secondary_text && (
                    <span className="suggestion-secondary">{suggestion.structured_formatting.secondary_text}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default AddressAutocomplete;
